<template>
  <div class="pricing">

    <!-- ─── Header ─── -->
    <section class="pricing-hero">
      <div class="pricing-container text-center">
        <p class="pricing-eyebrow">{{ t('pages./pricing.hero.eyebrow') }}</p>
        <h1 class="pricing-title">{{ t('pages./pricing.hero.title') }}</h1>
        <p class="pricing-sub">{{ t('pages./pricing.hero.subtitle') }}</p>
      </div>
    </section>

    <!-- ─── 方案卡片（單一方案）─── -->
    <section class="pricing-section">
      <div class="pricing-container">

        <div class="pricing-card-solo">
          <div class="pricing-card-main">
            <div>
              <p class="pricing-plan-label">{{ t('pages./pricing.plan.label') }}</p>
              <div class="pricing-price">
                <span class="pricing-price-num">{{ t('pages./pricing.plan.price') }}</span>
                <span class="pricing-price-period">{{ t('pages./pricing.plan.period') }}</span>
              </div>
              <p class="pricing-plan-desc" v-html="t('pages./pricing.plan.desc')"></p>
            </div>
            <a :href="`/${$i18n.locale}/signup`" class="pricing-btn pricing-btn-primary">{{ t('pages./pricing.plan.cta') }}</a>
          </div>
          <ul class="pricing-features pricing-features-solo">
            <li>{{ t('pages./pricing.plan.feature01') }}</li>
            <li>{{ t('pages./pricing.plan.feature02') }}</li>
            <li>{{ t('pages./pricing.plan.feature03') }}</li>
            <li>{{ t('pages./pricing.plan.feature04') }}</li>
          </ul>
        </div>

        <!-- AI credits 說明 -->
        <div class="pricing-note">
          <p>{{ t('pages./pricing.plan.note') }}</p>
        </div>
      </div>
    </section>

    <!-- ─── 企業陪跑服務（加值）─── -->
    <section class="pricing-section pricing-section-alt">
      <div class="pricing-container">
        <div class="pricing-addon">
          <p class="pricing-addon-label">{{ t('pages./pricing.addon.label') }}</p>
          <h2 class="pricing-addon-title">{{ t('pages./pricing.addon.title') }}</h2>
          <p class="pricing-addon-price">{{ t('pages./pricing.addon.price') }}</p>
          <p class="pricing-addon-desc">{{ t('pages./pricing.addon.desc') }}</p>
          <ul class="pricing-features" style="margin-top: 1.5rem;">
            <li>{{ t('pages./pricing.addon.feature01') }}</li>
            <li>{{ t('pages./pricing.addon.feature02') }}</li>
            <li>{{ t('pages./pricing.addon.feature03') }}</li>
            <li>{{ t('pages./pricing.addon.feature04') }}</li>
          </ul>
          <a :href="`mailto:hello@kikuflow.com?subject=${t('common.accompany-mailto-subject')}`" class="pricing-btn pricing-btn-outline" style="margin-top: 2rem;">
            {{ t('pages./pricing.addon.cta') }}
          </a>
        </div>
      </div>
    </section>

    <!-- ─── FAQ ─── -->
    <section class="pricing-section">
      <div class="pricing-container">
        <h2 class="pricing-faq-title">{{ t('pages./pricing.faq.title') }}</h2>
        <div class="pricing-faq-list">
          <div class="pricing-faq-item">
            <p class="pricing-faq-q">{{ t('pages./pricing.faq.q1') }}</p>
            <p class="pricing-faq-a">{{ t('pages./pricing.faq.a1') }}</p>
          </div>
          <div class="pricing-faq-item">
            <p class="pricing-faq-q">{{ t('pages./pricing.faq.q2') }}</p>
            <p class="pricing-faq-a">{{ t('pages./pricing.faq.a2') }}</p>
          </div>
          <div class="pricing-faq-item">
            <p class="pricing-faq-q">{{ t('pages./pricing.faq.q3') }}</p>
            <p class="pricing-faq-a">{{ t('pages./pricing.faq.a3') }}</p>
          </div>
          <div class="pricing-faq-item">
            <p class="pricing-faq-q">{{ t('pages./pricing.faq.q4') }}</p>
            <p class="pricing-faq-a">{{ t('pages./pricing.faq.a4') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ─── CTA ─── -->
    <section class="pricing-section pricing-section-dark">
      <div class="pricing-container text-center">
        <h2 class="pricing-cta-title">{{ t('pages./pricing.cta.title') }}</h2>
        <p class="pricing-cta-sub">{{ t('pages./pricing.cta.sub') }}</p>
        <a :href="`/${$i18n.locale}/signup`" class="pricing-btn pricing-btn-light pricing-btn-lg">{{ t('pages./pricing.cta.btn') }}</a>
      </div>
    </section>

  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
</script>

<style scoped>
/* ─── 基礎 ─── */
.pricing {
  font-family: 'Noto Sans TC', 'PingFang TC', 'Microsoft JhengHei', sans-serif;
  color: #1a1a1a;
  line-height: 1.8;
}

.pricing-container {
  max-width: 900px;
  margin: 0 auto;
  padding: 0 2rem;
}

.text-center { text-align: center; }

/* ─── Section ─── */
.pricing-section {
  padding: 6rem 0;
}

.pricing-section-alt {
  background-color: #fafafa;
}

.pricing-section-dark {
  background-color: #1a1a1a;
  color: #fff;
}

/* ─── Hero ─── */
.pricing-hero {
  padding: 8rem 0 5rem;
  background-color: #fff;
}

.pricing-eyebrow {
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: #888;
  margin-bottom: 1rem;
}

.pricing-title {
  font-size: clamp(2rem, 5vw, 3rem);
  font-weight: 700;
  line-height: 1.3;
  margin-bottom: 1rem;
}

.pricing-sub {
  font-size: 1.1rem;
  color: #555;
}

/* ─── 方案卡片（單一方案）─── */
.pricing-card-solo {
  display: flex;
  gap: 4rem;
  align-items: flex-start;
  padding: 3rem;
  border: 1.5px solid #1a1a1a;
  border-radius: 20px;
  background: #1a1a1a;
  color: #fff;
  margin-bottom: 3rem;
}

.pricing-card-main {
  display: flex;
  flex-direction: column;
  gap: 2rem;
  flex-shrink: 0;
  min-width: 260px;
}

.pricing-plan-label {
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #888;
  margin-bottom: 1rem;
}

.pricing-price {
  margin-bottom: 1rem;
}

.pricing-price-num {
  font-size: 2.5rem;
  font-weight: 700;
  line-height: 1;
  color: #fff;
}

.pricing-price-period {
  display: block;
  font-size: 0.9rem;
  color: #888;
  margin-top: 0.3rem;
}

.pricing-plan-desc {
  font-size: 0.95rem;
  color: #aaa;
  margin-bottom: 0;
  line-height: 1.8;
}

/* ─── 功能列表 ─── */
.pricing-features {
  list-style: none;
  padding: 0;
  margin: 0;
}

.pricing-features li {
  padding: 0.4rem 0;
  font-size: 0.95rem;
  color: #555;
  padding-left: 1.4rem;
  position: relative;
}

.pricing-features li::before {
  content: '✓';
  position: absolute;
  left: 0;
  color: #1a1a1a;
  font-weight: 700;
}

/* 深色卡片內的功能列表 */
.pricing-features-solo li {
  color: #bbb;
  border-top: 1px solid #333;
  padding: 0.75rem 0 0.75rem 1.4rem;
}

.pricing-features-solo li::before {
  color: #fff;
}

/* ─── 按鈕 ─── */
.pricing-btn {
  display: inline-block;
  padding: 0.85rem 2rem;
  border-radius: 100px;
  font-size: 1rem;
  font-weight: 600;
  text-decoration: none;
  text-align: center;
  transition: opacity 0.2s;
}

.pricing-btn:hover { opacity: 0.75; }

.pricing-btn-primary {
  background: #fff;
  color: #1a1a1a;
}

.pricing-btn-outline {
  border: 1.5px solid #1a1a1a;
  color: #1a1a1a;
}

.pricing-btn-light {
  background: #fff;
  color: #1a1a1a;
}

.pricing-btn-lg {
  padding: 1rem 2.8rem;
  font-size: 1.1rem;
  margin-top: 1.5rem;
}

/* ─── AI credits 說明 ─── */
.pricing-note {
  text-align: center;
  font-size: 0.95rem;
  color: #888;
  line-height: 1.9;
  padding: 2rem 0;
  border-top: 1px solid #eee;
}

.pricing-note p { margin: 0; }

/* ─── 陪跑加值服務 ─── */
.pricing-addon {
  padding: 3rem 2.5rem;
  border: 1.5px solid #e8e8e8;
  border-radius: 20px;
  background: #fff;
}

.pricing-addon-label {
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #9b8afb;
  margin-bottom: 0.5rem;
}

.pricing-addon-title {
  font-size: clamp(1.5rem, 3vw, 2rem);
  font-weight: 700;
  margin-bottom: 0.5rem;
}

.pricing-addon-price {
  font-size: 1.3rem;
  font-weight: 700;
  color: #555;
  margin-bottom: 1rem;
}

.pricing-addon-desc {
  font-size: 1rem;
  color: #555;
  line-height: 2;
  margin: 0;
}

/* ─── FAQ ─── */
.pricing-faq-title {
  font-size: clamp(1.4rem, 3vw, 2rem);
  font-weight: 700;
  margin-bottom: 2.5rem;
}

.pricing-faq-list {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.pricing-faq-item {
  padding: 1.5rem 0;
  border-bottom: 1px solid #eee;
}

.pricing-faq-q {
  font-weight: 700;
  font-size: 1rem;
  margin-bottom: 0.5rem;
  color: #1a1a1a;
}

.pricing-faq-a {
  font-size: 0.95rem;
  color: #666;
  line-height: 1.8;
  margin: 0;
}

/* ─── CTA ─── */
.pricing-cta-title {
  font-size: clamp(1.8rem, 4vw, 2.5rem);
  font-weight: 700;
  color: #fff;
  margin-bottom: 0.5rem;
}

.pricing-cta-sub {
  font-size: 1rem;
  color: #aaa;
  margin: 0;
}

/* ─── RWD ─── */
@media (max-width: 768px) {
  .pricing-card-solo {
    flex-direction: column;
    gap: 2rem;
  }

  .pricing-section {
    padding: 4rem 0;
  }

  .pricing-hero {
    padding: 6rem 0 3rem;
  }
}
</style>
